import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  Menu,
  Pagination,
  Skeleton,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  calculateContrastColor,
  lebaleStyle,
  pageScrollStyle,
} from "../../../../styles/styles";
import axios from "axios";
import { backend_api } from "../../../../static/server";
import {
  createTag,
  getAllTags,
  updateTag,
} from "../../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import ColorLensRoundedIcon from "@mui/icons-material/ColorLensRounded";
import FormatColorFillRoundedIcon from "@mui/icons-material/FormatColorFillRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { LoadingButton } from "@mui/lab";
import { CloseRounded, LockRounded } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  CHIP_PATTERNS,
  NoDataOptionField,
} from "../../../../static/staticdata";
import noOption from "../../../../assets/no-option-found.webp";
import { useSubscription } from "../../../Auth/SubscriptionContext";

const DefaultColors = [
  "#F87171",
  "#60A5FA",
  "#34D399",
  "#FBBF24",
  "#A78BFA",
  "#F97316",
];

const TagForm = () => {
  const dispatch = useDispatch();
  const { isLimitReached } = useSubscription();
  const [colorMenu, setColorMenu] = useState(null);
  const [selectedColor, setSelectedColor] = useState("#34D399");
  const [customColor, setCustomColor] = useState("#34D399");
  const [selectedChip, setSelectedChip] = useState(null);
  const [sameTagerror, setSameTagError] = useState(false);
  const [loadCheckSameTag, setLoadCheckSameTag] = useState(false);
  const [tagId, setTagId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const { token } = useSelector((data) => data.user);
  const { allTags, tagLoading, Tagpagination } = useSelector(
    (state) => state.contact
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      metadata: { color: "" },
    },
  });

  useEffect(() => {
    fetchAllTags(1, pageSize);
  }, []);

  const fetchAllTags = (page = 1, pageSizeParam = pageSize, search = "") => {
    setCurrentPage(page);
    dispatch(
      getAllTags({
        url: `${backend_api}info-tags/`,
        token: token,
        page: page,
        pageSize: pageSizeParam,
        search: search,
      })
    );
  };

  // Calculate total pages
  const totalPages = Math.ceil((Tagpagination?.count || 0) / pageSize);

  const onSubmit = async (data) => {
    try {
      const body = { ...data, metadata: { color: selectedColor } };
      setLoadCheckSameTag(true);

      if (selectedChip && tagId) {
        await dispatch(
          updateTag({
            url: `${backend_api}info-tags/${tagId}/`,
            data: body,
            token: token,
          })
        ).unwrap();

        toast.success("Tag updated successfully");
      } else {
        await dispatch(
          createTag({
            url: `${backend_api}info-tags/`,
            body: body,
            token: token,
          })
        ).unwrap();
      }
      // Refresh the tags list after successful operation
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          `Failed to ${tagId ? "update" : "create"} tag`
      );
    } finally {
      fetchAllTags(currentPage, pageSize, searchValue);
      // Reset form and state
      reset();
      setSameTagError(false);
      setSelectedColor("#34D399");
      setTagId(null);
      setSelectedChip(null);
      setLoadCheckSameTag(false);
    }
  };

  const handleTagChange = async (e, value) => {
    e.preventDefault();
    setSearchValue(value);
    setCurrentPage(1); // Reset to first page when searching

    // If the input is empty, fetch all tags
    if (!value.trim()) {
      fetchAllTags(1, pageSize);
      setSameTagError(false);
      return;
    }

    setLoadCheckSameTag(true);
    try {
      const res = await axios.get(
        `${backend_api}info-tags/?tag_name=${value}`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Only set error if not in edit mode (no selected chip)
      if (res.data.results.length > 0 && !selectedChip) {
        setSameTagError(true);
      } else {
        setSameTagError(false);
      }

      // Update the tag list with search results using pagination
      fetchAllTags(1, pageSize, value);

      return res.data;
    } catch (error) {
    } finally {
      setLoadCheckSameTag(false);
    }
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    setColorMenu(null);
  };

  const handleCustomColorChange = (e) => {
    setCustomColor(e.target.value);
    setSelectedColor(e.target.value);
  };

  const deleteTag = async (id) => {
    try {
      await axios.delete(`${backend_api}info-tags/${id}/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Tag deleted successfully");

      // Check if we need to go to previous page after deletion
      const remainingItemsOnCurrentPage = allTags?.length - 1;
      let targetPage = currentPage;

      if (remainingItemsOnCurrentPage === 0 && currentPage > 1) {
        targetPage = currentPage - 1;
      }

      // Refresh the tags list after deletion
      fetchAllTags(targetPage, pageSize, searchValue);
    } catch (error) {
      toast.error("Failed to delete the tag. Please try again");
    } finally {
      // Reset form and state
      reset();
      setSameTagError(false);
      setSelectedColor("#34D399");
      setTagId(null);
      setSelectedChip(null);
    }
  };

  const handleChipSeletct = (chip) => {
    if (selectedChip?.id === chip.id) {
      // If clicking the same chip, deselect it
      setSelectedChip(null);
      setTagId(null);
      reset();
      setSameTagError(false);
      setSelectedColor("#34D399");
    } else {
      // Select the new chip
      setSelectedChip(chip);
      setTagId(chip?.id);
      setSelectedColor(chip?.metadata?.color || "#34D399");
      setValue("name", chip?.name);
      setValue("metadata.color", chip?.metadata?.color);
      setSameTagError(false);
    }
  };

  const handlePaginationChange = (event, value) => {
    event.preventDefault();
    fetchAllTags(value, pageSize, searchValue);
  };

  const handleReset = () => {
    setSelectedColor("#34D399");
    setSelectedChip(null);
    setTagId(null);
    setSearchValue("");
    setCurrentPage(1);
    reset();
    setSameTagError(false);
    fetchAllTags(1, pageSize);
  };

  return (
    <>
      <Box sx={{ p: "1rem" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontSize: "0.8rem", color: "#5c5c5c" }}
          >
            With tag make your contact list unique and easy to find.
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "end",
                gap: "1rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.1rem",
                  width: "80%",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <label htmlFor="Create_new_tag" style={lebaleStyle}>
                    Tag Name:
                  </label>
                  {errors?.name ? (
                    <Typography
                      variant="body1"
                      color="error"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      Tag name required for create tag.
                    </Typography>
                  ) : sameTagerror ? (
                    <Typography
                      variant="body1"
                      color="error"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      This tag already exists. Please enter another!
                    </Typography>
                  ) : null}
                </Box>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      error={Boolean(errors?.name) || sameTagerror}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                        handleTagChange(e, value);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          paddingLeft: "0",
                        },
                        "& .MuiInputAdornment-root": {
                          marginRight: "0.5rem",
                          borderRight: "1px solid #ccc",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            sx={{ px: "0.5rem" }}
                          >
                            <Box
                              onClick={(e) => setColorMenu(e.currentTarget)}
                              sx={{
                                width: "1.8rem",
                                height: "1.8rem",
                                borderRadius: "50%",
                                cursor: "pointer",
                                bgcolor: selectedColor,
                                border: "1.8px solid #fff",
                                outline: `2px solid ${selectedColor}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <ColorLensRoundedIcon
                                sx={{ color: "#fff", fontSize: "1rem" }}
                              />
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Menu
                  anchorEl={colorMenu}
                  open={Boolean(colorMenu)}
                  onClose={() => setColorMenu(null)}
                  TransitionComponent={Fade}
                  TransitionProps={{ timeout: 300 }}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        overflow: "hidden",
                        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                        "& .MuiList-root": {
                          p: 0,
                        },
                      },
                    },
                  }}
                  transformOrigin={{
                    horizontal: "left",
                    vertical: "center",
                  }}
                  anchorOrigin={{ horizontal: "right", vertical: "center" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "0.5rem",
                      p: "0.5rem",
                    }}
                  >
                    {DefaultColors.map((color, i) => (
                      <Slide
                        key={i}
                        direction="up"
                        in={Boolean(colorMenu)}
                        timeout={200 + i * 50}
                      >
                        <Box
                          onClick={() => handleColorChange(color)}
                          sx={{
                            height: "1.5rem",
                            width: "1.5rem",
                            borderRadius: "50%",
                            bgcolor: color,
                            cursor: "pointer",
                            border:
                              color === selectedColor
                                ? "2px solid black"
                                : "none",
                            transform: "translateY(10px)",
                            transition: "transform 0.3s ease-in-out",
                            "&:hover": {
                              transform: "translateY(0)",
                            },
                          }}
                        />
                      </Slide>
                    ))}

                    {/* Custom Color Picker */}
                    <Slide direction="up" in={Boolean(colorMenu)} timeout={500}>
                      <IconButton component="label" size="small">
                        <FormatColorFillRoundedIcon sx={{ fontSize: "1rem" }} />

                        <input
                          type="color"
                          value={customColor}
                          onChange={handleCustomColorChange}
                          style={{
                            position: "absolute",
                            opacity: 0,
                            width: "100%",
                            height: "100%",
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    </Slide>
                  </Box>
                </Menu>
              </Box>
              <Box sx={{ width: "20%" }}>
                <Tooltip
                  title={
                    !tagId && isLimitReached("current_tags_limit")
                      ? "You’ve reached your Tag limit. Upgrade your plan to create more Tags."
                      : ""
                  }
                  disableHoverListener={
                    tagId || !isLimitReached("current_tags_limit")
                  }
                >
                  <span>
                    <LoadingButton
                      variant="outlined"
                      loading={tagLoading || loadCheckSameTag}
                      size="small"
                      type="submit"
                      sx={{
                        fontSize: "0.8rem",
                        textTransform: "capitalize",
                        cursor:
                          !tagId && isLimitReached("current_tags_limit")
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          !tagId && isLimitReached("current_tags_limit")
                            ? 0.8
                            : 1,
                      }}
                      disabled={
                        sameTagerror ||
                        (!tagId && isLimitReached("current_tags_limit")) // disable only for new tag creation
                      }
                      endIcon={
                        !tagId && isLimitReached("current_tags_limit") ? (
                          <LockRounded
                            sx={{ fontSize: "1rem", color: "error.main" }}
                          />
                        ) : null
                      }
                    >
                      {!tagId && isLimitReached("current_tags_limit")
                        ? "Limit Reached"
                        : selectedChip && tagId
                        ? "Update Tag"
                        : "Create Tag"}
                    </LoadingButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </form>
          <Divider orientation="horizontal " flexItem />
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "1rem",
                mb: "0.5rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "1rem",
                  width: "100%",
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      textWrap: "nowrap",
                    }}
                  >
                    Manage Your Tags ({Tagpagination?.count || 0} total)
                    {searchValue && (
                      <Typography
                        component="span"
                        sx={{ fontSize: "0.8rem", color: "#666", ml: 1 }}
                      >
                        - Search: "{searchValue}"
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <Box>
                  {selectedChip && tagId && (
                    <Box sx={{ display: "flex", gap: "1rem" }}>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteRoundedIcon />}
                        size="small"
                        onClick={() => deleteTag(tagId)}
                      >
                        Delete selected tag
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<CloseRounded />}
                        size="small"
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.3rem",
                height: "12rem",
                overflowY: "auto",
                scrollBehavior: "smooth",
                p: "0.5rem",
                ...pageScrollStyle,
              }}
            >
              {tagLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.6rem",
                  }}
                >
                  {CHIP_PATTERNS.map((row, rowIndex) => (
                    <Box
                      key={rowIndex}
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {row?.map((width, chipIndex) => (
                        <Skeleton
                          key={chipIndex}
                          variant="rectangular"
                          height={28}
                          width={width}
                          sx={{ borderRadius: "12px" }}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              ) : allTags?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {allTags?.map((item) => {
                    const isSelected = selectedChip?.id === item.id;
                    return (
                      <Typography
                        key={item?.id}
                        onClick={() => handleChipSeletct(item)}
                        sx={{
                          px: "0.5rem",
                          py: "0.3rem",
                          borderRadius: "3rem",
                          bgcolor: item?.metadata?.color || "gray",
                          color: calculateContrastColor(
                            item?.metadata?.color || "#fff"
                          ),
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          border: "1px solid",
                          borderColor: calculateContrastColor(
                            item?.metadata?.color || "#fff"
                          ),
                          outline: isSelected ? "2px solid #00B3B0" : "none",
                          transition: "background-color 0.2s ease-in-out",
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: item?.metadata?.color || "gray",
                            opacity: "0.8",
                          },
                        }}
                      >
                        {item?.name}
                      </Typography>
                    );
                  })}
                </Box>
              ) : (
                <NoDataOptionField
                  imgSrc={noOption}
                  text="Oops! Looks like there are no tags to display"
                />
              )}
            </Box>
          </Box>
        </Box>
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: "1.5rem",
              gap: "1rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "0.8rem", color: "#666" }}
            >
              Page {currentPage} of {totalPages} ({Tagpagination?.count || 0}{" "}
              total tags)
            </Typography>
            <Stack spacing={1}>
              <Pagination
                count={totalPages}
                variant="outlined"
                color="primary"
                page={currentPage}
                onChange={handlePaginationChange}
                disabled={tagLoading}
                size="small"
              />
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
};

export default TagForm;
